/**
 * The page frame every screen uses — docs/LAYOUT.md.
 *
 * `Page` caps the content column so rows never span the full width of a wide
 * monitor (which strands a row's metadata a screen-width from its title).
 * `PageHeader` puts every screen's actions in the same place, and `Section`
 * gives it one vertical rhythm.
 */
import { createContext, type ReactNode, useContext } from 'react'
import { cn } from '@/lib/utils'

/**
 * `width` is chosen by what the page holds, not by the screen — and always
 * centred, so content never runs to the edges of a wide window:
 *   - "wide"     lists, boards, dashboards
 *   - "settings" choices and forms — a row holding one control has no business
 *                being 1200px wide. Everything under /settings uses it.
 * Lists cap at 1200px — docs/UX-GUIDE.md's widest content.
 *   - "reading"  long prose
 */
const PAGE_WIDTH = {
  wide: 'max-w-[1200px]',
  settings: 'max-w-[820px]',
  reading: 'max-w-[var(--container-measure)]',
} as const

export type PageWidth = keyof typeof PAGE_WIDTH

/**
 * The width a `Page` takes when it doesn't name one. A whole section sets it
 * once — src/routes/settings.tsx sets "settings" for everything under
 * /settings — so no screen in it can forget.
 */
const DefaultPageWidth = createContext<PageWidth>('wide')
export const PageWidthDefault = DefaultPageWidth.Provider

export function Page({
  children,
  className,
  width,
}: {
  children: ReactNode
  className?: string
  width?: PageWidth
}) {
  const sectionWidth = useContext(DefaultPageWidth)
  return (
    <div
      data-slot="page"
      data-width={width ?? sectionWidth}
      className={cn(
        'mx-auto flex w-full flex-col gap-8',
        PAGE_WIDTH[width ?? sectionWidth],
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * The top of a screen: its actions, at the right, with the stats or the list
 * straight after. The page's name is the header's title (the shell's `<h1>`),
 * so a screen never repeats it, nor adds a line describing itself — Gideon's
 * call (docs/DECISIONS.md D-026). `view` names a view *within* the page that
 * isn't the page itself ("Recently deleted clients"). With neither, it
 * renders nothing and the content starts at the top.
 */
export function PageHeader({ view, actions }: { view?: string; actions?: ReactNode }) {
  if (!view && !actions) return null
  return (
    <div
      data-slot="page-header"
      // 16px to what follows, not the page's 32: a row of buttons belongs to
      // the content under it, not a section of its own.
      className="-mb-4 flex flex-wrap items-center justify-between gap-3"
    >
      {view ? <h2 className="text-lg font-semibold">{view}</h2> : null}
      {actions ? (
        <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  )
}

export function Section({
  title,
  actions,
  children,
}: {
  title?: string
  actions?: ReactNode
  children: ReactNode
}) {
  return (
    <section data-slot="section" className="flex flex-col gap-4">
      {title || actions ? (
        <div className="flex items-center justify-between gap-4">
          {title ? <h3 className="text-lg font-semibold">{title}</h3> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  )
}

/**
 * A list that reads as one object: a single bordered surface with hairline
 * dividers, rather than rows floating on the page background.
 */
export function ListCard({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      data-slot="list-card"
      className={cn('divide-y overflow-clip rounded-lg border bg-card shadow-resting', className)}
    >
      {children}
    </div>
  )
}

export function ListRow({
  leading,
  title,
  meta,
  trailing,
  onClick,
  wrapMeta = false,
}: {
  leading?: ReactNode
  title: ReactNode
  meta?: ReactNode
  trailing?: ReactNode
  onClick?: () => void
  /**
   * Let the meta line wrap instead of ending in "…". For a sentence that
   * explains the row (Settings' destinations), where a phone cut it mid-word.
   * Leave it off for record lists, where one tidy line per row matters more.
   */
  wrapMeta?: boolean
}) {
  const content = (
    <>
      {leading ? <div className="shrink-0">{leading}</div> : null}
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="truncate text-base font-semibold">{title}</span>
        {meta ? (
          <span className={cn('text-sm text-muted-foreground', !wrapMeta && 'truncate')}>
            {meta}
          </span>
        ) : null}
      </div>
      {trailing ? <div className="shrink-0">{trailing}</div> : null}
    </>
  )

  if (!onClick) {
    return <div className="group/row flex items-center gap-4 px-5 py-4 md:px-6">{content}</div>
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className="group/row flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 md:px-6"
    >
      {content}
    </button>
  )
}

/**
 * A setting: label and explanation on the left, the control anchored to the
 * right edge of the row. A control alone on a page background is the single
 * easiest way to make a screen look unfinished — it has nothing to belong to
 * and nothing to line up with. Put it in a row.
 */
export function SettingRow({
  label,
  description,
  control,
}: {
  label: string
  description?: string
  control: ReactNode
}) {
  return (
    <div
      data-slot="setting-row"
      className="flex flex-wrap items-center justify-between gap-4 px-5 py-5 md:px-6"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-base font-semibold">{label}</span>
        {description ? (
          <span className="max-w-measure text-sm text-muted-foreground">{description}</span>
        ) : null}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  )
}

/**
 * One grid for every group of stats, so a card is the same size on every
 * screen — two stats do not stretch to half the page just because there are
 * two of them. Single column on phones (docs/LAYOUT.md).
 */
export function StatGrid({ children }: { children: ReactNode }) {
  return (
    <div data-slot="stat-grid" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {children}
    </div>
  )
}

/** Label · value · optional caption. Not a big empty box. */
export function StatCard({
  label,
  value,
  caption,
  /** The one stat on the screen that matters most. Use it once, or not at all. */
  emphasis = false,
}: {
  label: string
  value: ReactNode
  caption?: string
  emphasis?: boolean
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-card p-5 shadow-resting md:px-6">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span
        className={cn(
          'text-2xl font-semibold tabular-nums tracking-[-0.01em]',
          emphasis && 'text-primary',
        )}
      >
        {value}
      </span>
      {caption ? <span className="text-sm text-muted-foreground">{caption}</span> : null}
    </div>
  )
}
